import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { UserPlus, UserMinus } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { followUser, unfollowUser, isFollowing } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';

interface FollowButtonProps {
  userId: string;
  initialFollowingState?: boolean;
  className?: string;
  isFollowing?: boolean; // Added to make the component compatible with the Profile page usage
  onFollowChange?: (isFollowing: boolean) => void; // Callback to notify parent component of follow status change
}

const FollowButton = ({ 
  userId, 
  initialFollowingState = false, 
  isFollowing: externalIsFollowing, 
  className = "",
  onFollowChange 
}: FollowButtonProps) => {
  // Use external isFollowing state if provided, otherwise use initialFollowingState
  const [isFollowingUser, setIsFollowingUser] = useState<boolean>(externalIsFollowing !== undefined ? externalIsFollowing : initialFollowingState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(true);
  const { toast } = useToast();
  const { user } = useAuth(); // No need to destructure refreshProfile here
  const navigate = useNavigate();

  // Check if the current user is following the user
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (user && userId && user.id !== userId) {
        try {
          const following = await isFollowing(user.id, userId);
          setIsFollowingUser(following);
        } catch (error) {
          console.error("Error checking follow status:", error);
        } finally {
          setIsCheckingStatus(false);
        }
      } else {
        setIsCheckingStatus(false);
      }
    };

    // Only check follow status if an external isFollowing state isn't provided
    if (externalIsFollowing === undefined) {
      checkFollowStatus();
    } else {
      setIsCheckingStatus(false);
    }
  }, [user, userId, externalIsFollowing]);

  // Update internal state when external isFollowing prop changes
  useEffect(() => {
    if (externalIsFollowing !== undefined) {
      setIsFollowingUser(externalIsFollowing);
    }
  }, [externalIsFollowing]);

  const handleFollowToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent default behavior to avoid any page reloads
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to follow other users",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    setIsLoading(true);
    
    try {
      if (isFollowingUser) {
        // Optimistic UI update - immediately show unfollow state
        setIsFollowingUser(false);
        if (onFollowChange) {
          onFollowChange(false);
        }
        
        // Unfollow the user
        const success = await unfollowUser(user.id, userId);
        if (!success) {
          // Revert the optimistic update if the API call fails
          setIsFollowingUser(true);
          if (onFollowChange) {
            onFollowChange(true);
          }
          throw new Error("Failed to unfollow user");
        }
        
        toast({
          title: "Unfollowed",
          description: "You have unfollowed this user",
          duration: 2000,
        });
      } else {
        // Optimistic UI update - immediately show follow state
        setIsFollowingUser(true);
        if (onFollowChange) {
          onFollowChange(true);
        }
        
        // Follow the user
        const success = await followUser(user.id, userId);
        if (!success) {
          // Revert the optimistic update if the API call fails
          setIsFollowingUser(false);
          if (onFollowChange) {
            onFollowChange(false);
          }
          throw new Error("Failed to follow user");
        }
        
        toast({
          title: "Following",
          description: "You are now following this user",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error("Error toggling follow status:", error);
      toast({
        title: "Error",
        description: "Failed to update follow status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show follow button for the current user
  if (user && userId === user.id) {
    return null;
  }

  return (
    <Button
      variant={isFollowingUser ? "outline" : "default"}
      size="sm"
      disabled={isLoading || isCheckingStatus}
      onClick={handleFollowToggle}
      className={className}
    >
      {isLoading ? (
        "Loading..."
      ) : isCheckingStatus ? (
        <span className="flex items-center">
          <span className="h-3 w-3 mr-2 rounded-full bg-vibebops-highlight animate-pulse"></span>
          Loading
        </span>
      ) : isFollowingUser ? (
        <>
          <UserMinus className="mr-2 h-4 w-4" />
          Unfollow
        </>
      ) : (
        <>
          <UserPlus className="mr-2 h-4 w-4" />
          Follow
        </>
      )}
    </Button>
  );
};

export default FollowButton;
